import React, { useState, KeyboardEvent, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, IconButton, TextField, InputAdornment, Tooltip } from '@mui/material';
import { mdiSend, mdiStop } from '@mdi/js';
import { t } from '@lingui/core/macro';
import MdiIcon from '../mdi/MdiIcon';

interface ChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  disabled?: boolean;
}

export interface ChatInputHandle {
  /** Move keyboard focus into the composer. */
  focus(): void;
}

/**
 * The composer.
 *
 * Exposed through a ref rather than left to the browser so that opening the
 * panel can put the caret here directly. Nothing else on screen is a
 * keyboard-reachable equivalent -- without this, opening the chat with `/` left
 * focus on the document, and the first Tab went to the browser chrome rather
 * than to the thing the user had just asked to talk to.
 */
export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  function ChatInput({ onSend, onStop, disabled }, ref) {
    const [message, setMessage] = useState('');
    const inputRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const el = inputRef.current;
        if (!el) return;
        // `inputRef` on a multiline TextField forwards to the textarea itself, so
        // in practice this is usually the element rather than a container to
        // search inside.
        if (el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement) {
          el.focus();
          return;
        }
        el.querySelector<HTMLTextAreaElement>('textarea')?.focus();
      },
    }));

    const handleSend = () => {
      if (message.trim() && !disabled) {
        onSend(message.trim());
        setMessage('');
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    };

    // While a reply is streaming the adornment becomes a Stop control, so the
    // send button is never both disabled and unreachable.
    const isStreaming = Boolean(disabled && onStop);

    return (
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          inputRef={inputRef}
          placeholder={t`Type a message...`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={Boolean(disabled && !onStop)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {isStreaming ? (
                    <Tooltip title={t`Stop generating`}>
                      <IconButton onClick={onStop} color="error" aria-label={t`Stop generating`}>
                        <MdiIcon d={mdiStop} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <IconButton
                      onClick={handleSend}
                      disabled={disabled || !message.trim()}
                      color="primary"
                      aria-label={t`Send message`}
                    >
                      <MdiIcon d={mdiSend} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
    );
  },
);
